import React, { useMemo, useState } from 'react';
import StaticDropDown from '../../staticDropDown';
import DropDownSelect, { DropDownColorTheme } from '../../dropDownSelect';
import { DashboardCardTitle, Dashboard_Not_Available_Label } from '..';

import './cards.css';
import {
    LAHJJurisdictions,
    WaterJurisdictions,
} from '../../../constants/jurisdictions';
import {
    PropertyScoutFieldNames,
    updatePropertyScoutFields,
} from '../../../utilities/scoutHelpers';
import { objectDeepClone } from '../../../utilities/generalHelpers';

const isHighSchool = (school) => school.max_assigned_grade === '12';
const isElementarySchool = (school) =>
    school.max_assigned_grade <= 5 || school.name.includes('Elementary');

const CharacteristicsCard = ({ property: { propertyDetails }, onUpdate }) => {
    const { PropertyAddress, AssignedSchool, PropertyScoutFields } =
    propertyDetails;
    const {
        generic_data: { jurisdictions },
    } = PropertyScoutFields;
    const [selectedJurisdictions, setSelectedJurisdictions] = useState({
        lahj: jurisdictions.lahj || '',
        water: jurisdictions.water || '',
    });

    const feasibility = propertyDetails?.PropertyFeasibility
        ? propertyDetails?.PropertyFeasibility?.asce_7_16_ss
        : null;
    const highSchools = useMemo(
        () =>
            AssignedSchool.filter((school) => isHighSchool(school)).map(
                (school) => school.name
            ),
        [AssignedSchool]
    );

    const elementarySchools = useMemo(
        () =>
            AssignedSchool.filter((school) => isElementarySchool(school)).map(
                (school) => school.name
            ),
        [AssignedSchool]
    );

    const renderRow = (label, value) => (
        <>
            <div style={{ wordWrap: 'break-word', hyphens: 'manual' }}>{label}</div>
            <div style={{ fontWeight: 500, wordWrap: 'break-word' }}>{value}</div>
        </>
    );

    const renderSchoolsRow = (label, schools, keyPrefix) => {
        if (!schools.length) {
            return renderRow(label, Dashboard_Not_Available_Label);
        } else if (schools.length === 1) {
            return renderRow(label, schools[0]);
        } else
            return (
                <>
                    <div>{label}</div>
                    <div>
                        <StaticDropDown values={schools} keyPrefix={keyPrefix} />
                    </div>
                </>
            );
    };

    const handleUpdateJurisdiction = (jurisdictionType, opt) => {
        const updatedGenericData = objectDeepClone(
            PropertyScoutFields.generic_data
        );
        updatedGenericData.jurisdictions[jurisdictionType] = opt;
        onUpdate(
            'PropertyScoutFields',
            updatePropertyScoutFields(
                propertyDetails.PropertyScoutFields,
                PropertyScoutFieldNames.GenericData,
                updatedGenericData
            )
        );
        setSelectedJurisdictions((val) => ({ ...val, [jurisdictionType]: opt }));
    };

    const renderJurisdictionsRow = (jurisdictionType) => {
        let label;
        let options;
        if (jurisdictionType === 'water') {
            label = 'Water Jurisdiction';
            options = WaterJurisdictions;
        } else {
            label = 'LAHJ';
            options = LAHJJurisdictions;
        }
        return (
            <>
                <div>{label}</div>
                <div>
                    <DropDownSelect
                        options={options}
                        selected={selectedJurisdictions[jurisdictionType]}
                        onSelect={(opt) => handleUpdateJurisdiction(jurisdictionType, opt)}
                        label="Select Jurisdiction"
                        colorTheme={DropDownColorTheme.dark}
                    />
                </div>
            </>
        );
    };

    // what's going on with the \u00ab? this is unicode for &shy; which
    // allows label to be conditionally wrapped with a hypen
    return (
        <div>
            <DashboardCardTitle text="Characteristics" />
            <div className="pe-1 characteristicsGrid">
                {renderRow(
                    'Neigh\u00adbor\u00adhood',
                    PropertyAddress?.neighborhood_name || Dashboard_Not_Available_Label
                )}
                {renderSchoolsRow('High School', highSchools, 'highschool')}
                {renderSchoolsRow(
                    'Element\u00adary School',
                    elementarySchools,
                    'elementary'
                )}
                {renderRow('USGS ASCE 7-16 SS', feasibility || 'not available')}
                {renderJurisdictionsRow('lahj')}
                {renderJurisdictionsRow('water')}
            </div>
        </div>
    );
};

export default CharacteristicsCard;
